/**
 * ControlPanel API (`/alert-recipients`) クライアント (SBI-6)。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 所属区所 (depot_id) 単位の通知メール宛先を CRUD する。
 *   同一 depot_id に複数 email を登録できる (複数アドレス登録可)。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type {
  AlertRecipient,
  AlertRecipientCreateRequest,
  AlertRecipientDeleteResponse,
  AlertRecipientListResponse,
  ApiErrorBody,
} from './types';

export interface AlertRecipientsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class AlertRecipientsClient {
  private readonly options: AlertRecipientsClientOptions;

  constructor(options: AlertRecipientsClientOptions) {
    this.options = options;
  }

  /**
   * GET /alert-recipients (任意で depot_id 絞り込み)
   *
   * depot_id を指定した場合は当該所属区所の宛先のみ、省略時は全件を返す。
   */
  async list(depotId?: string): Promise<AlertRecipientListResponse> {
    const path = depotId
      ? `alert-recipients?depot_id=${encodeURIComponent(depotId)}`
      : 'alert-recipients';
    return this.request<AlertRecipientListResponse>('GET', path);
  }

  /** POST /alert-recipients (通知先メール宛先を登録 / 上書き) */
  async create(payload: AlertRecipientCreateRequest): Promise<AlertRecipient> {
    return this.request<AlertRecipient>('POST', 'alert-recipients', payload);
  }

  /** DELETE /alert-recipients?depot_id=&email= (通知先メール宛先を削除) */
  async remove(depotId: string, email: string): Promise<AlertRecipientDeleteResponse> {
    const path = `alert-recipients?depot_id=${encodeURIComponent(
      depotId,
    )}&email=${encodeURIComponent(email)}`;
    return this.request<AlertRecipientDeleteResponse>('DELETE', path);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? 'API リクエストに失敗しました';
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}
